import React from "react";
import ScheduleField from "components/ScheduleField";
import BillingPeriodField from "components/BillingPeriodField";
import AmountField from "components/AmountField";
import get from "lodash/get";
import classnames from "classnames";
import isEmpty from "lodash/isEmpty";
import { useFormikContext } from "formik";
import { useIsAuthed } from "utils/selectors";
import { getFieldPath } from "utils/form";
import { SCHEDULE_TYPE } from "utils/constants/price";
import { deconstructFormattedPrice, getIntervalLabel } from "utils/price";
import { TEST_MODE_BADGE } from "utils/constants/payment";
import {
  formattedPriceToUnitAmount,
  formatUnitAmount,
  getConfigForCurrency
} from "components/FormerEditor/common/currency";
import Pill, { PILL_THEME } from "components/Pill";

const FlexPrice = ({ name }) => {
  const isAuthed = useIsAuthed();
  const { values } = useFormikContext();

  const formattedPricePath = getFieldPath(name, `formattedPrice`);
  const currencyPath = getFieldPath(name, `currency`);

  const recurringPath = getFieldPath(name, `recurring`);
  const recurringIntervalPath = getFieldPath(name, `recurring.interval`);
  const recurringIntervalCountPath = getFieldPath(
    name,
    `recurring.interval_count`
  );

  const formattedPrice = get(values, formattedPricePath);
  const currency = get(values, currencyPath);
  const recurringInterval = get(values, recurringIntervalPath);
  const recurringIntervalCount = get(values, recurringIntervalCountPath);

  const scheduleValue = isEmpty(recurringInterval)
    ? SCHEDULE_TYPE.ONE_TIME
    : SCHEDULE_TYPE.RECURRING;

  let fields = [
    <AmountField
      hideLabel
      key={currency}
      value={formattedPrice}
      name={formattedPricePath}
      currency={currency}
      currencyPath={currencyPath}
      recurringIntervalPath={recurringIntervalPath}
      recurringInterval={recurringInterval}
    />
  ];

  fields.push(
    <ScheduleField label="Billing" name={recurringPath} value={scheduleValue} />
  );

  if (!isEmpty(recurringInterval)) {
    fields.push(
      <BillingPeriodField
        label="Every"
        intervalName={recurringIntervalPath}
        intervalCountName={recurringIntervalCountPath}
        value={recurringInterval}
      />
    );
  }

  /**
   * Get currency config
   */
  const currencyConfig = getConfigForCurrency(currency);
  const symbolValue = currencyConfig.symbol;

  /**
   * Prepare formattedValue
   * - Need fallback to show when theres nothing in the input
   * - Presentation value to only be the number
   * - Use the currency config symbol for the symbol value
   */
  const { formatted: formattedFallback } = deconstructFormattedPrice(
    formatUnitAmount({
      amount: 0,
      currency
    })
  );
  const { formatted } = deconstructFormattedPrice(formattedPrice);
  const formattedValue = formatted || formattedFallback;

  /**
   * Prepare presentation value
   */
  const unitAmount = formattedPriceToUnitAmount({
    formattedPrice: formattedValue,
    currency
  });
  const { formatted: amountToPresent } = deconstructFormattedPrice(
    formatUnitAmount({
      amount: unitAmount,
      currency
    })
  );

  const intervalLabel = getIntervalLabel(
    recurringInterval,
    recurringIntervalCount
  );

  const badge = isAuthed ? {} : TEST_MODE_BADGE;
  const banner = {
    label: ""
  };

  const hasBannerLabel = banner && banner.label;
  const hasBadgeLabel = badge && badge.label;

  return (
    <div className="w-100" style={{ maxWidth: 390 }}>
      <div
        className="pa3 br2"
        style={{
          border: "1px solid #E9E9ED",
          boxShadow: "0 5px 18px 0 rgb(0 0 0 / 5%)",
          borderColor: "#E9E9ED",
          borderRadius: 8,
          borderWidth: 2
        }}
      >
        <div
          className={classnames("w-100 flex flex-row items-center", {
            "justify-end": !hasBannerLabel && hasBadgeLabel
          })}
        >
          {hasBannerLabel && (
            <div className="w-100">
              <div className="f5 fw6 black-50">{banner.label}</div>
            </div>
          )}
          {hasBadgeLabel && (
            <Pill
              theme={PILL_THEME.SLIM}
              status={badge.status}
              copy={badge.label}
              customClasses={{
                container: "flex-shrink-0",
                copy: "f7 pa2 br2 fw6 flex-shrink-0 items-center"
              }}
            />
          )}
        </div>
        <div className="fw4 f4 pv3 tc flex flex-row justify-center">
          {symbolValue && (
            <div key={symbolValue} style={{ fontSize: 32 }} className="pr1 pt1">
              {symbolValue}
            </div>
          )}
          <div style={{ fontSize: 64 }} className="fw5 f2 lh-solid truncate">
            {amountToPresent}
          </div>
          {intervalLabel && <div className="f4 pl1 mt2">{intervalLabel}</div>}
        </div>
        {fields.map((node, nodeIx) => (
          <div key={nodeIx} className={nodeIx ? "pt3" : ""}>
            {node}
          </div>
        ))}
      </div>
    </div>
  );
};

export default FlexPrice;
